import { useEffect, useState } from 'react';
import { getDigestHistory } from '../api/skillScoutApi';
import { EmptyState } from '../components/EmptyState';
import type { DigestLogEntry } from '../types/api';

const TYPE_LABEL: Record<string, string> = {
  weekly: 'Weekly Digest',
  threshold_alert: 'Threshold Alert',
};

const TYPE_BADGE_CLASS: Record<string, string> = {
  weekly: 'bg-blue-600 text-blue-100',
  threshold_alert: 'bg-orange-500 text-orange-100',
};

export function DigestHistory() {
  const [entries, setEntries] = useState<DigestLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDigestHistory(20)
      .then(setEntries)
      .catch((e: unknown) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Digest History</h1>
          <p className="text-gray-400 text-sm mt-1">
            Past weekly digests and threshold alerts
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-900/40 border border-red-700 p-4 text-sm text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-gray-400 text-sm">Loading…</p>
        ) : entries.length === 0 ? (
          <EmptyState message="No digests sent yet." />
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => {
              const badgeClass =
                TYPE_BADGE_CLASS[entry.type] ?? 'bg-gray-500 text-gray-100';
              const label = TYPE_LABEL[entry.type] ?? entry.type;
              const sentDate = new Date(entry.sent_at).toLocaleString();

              return (
                <div
                  key={entry.id}
                  className="rounded-xl bg-surface p-4 flex items-center justify-between gap-4 flex-wrap"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${badgeClass}`}
                      data-digest-type={entry.type}
                    >
                      {label}
                    </span>
                    <span className="text-sm text-gray-400">{sentDate}</span>
                  </div>
                  <span className="text-xs text-gray-600 font-mono">{entry.id}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
